import { MutationOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { postList } from 'Services';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import { callAll, RQ_ADD_LIST, RQ_ALL_LIST, RQ_LIST_ENTRIES } from 'Utils';
import { AxiosResponse } from 'axios';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import { useTranslation } from 'react-i18next';

function useAddNewList({
    mutationOptions
}: {
    mutationOptions: Omit<MutationOptions<AxiosResponse<List>, any, any, any>, 'mutationKey'>;
}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { addTechnicalErrorModal } = useReduxModal();
    const { addAlert } = useAlertBanner();
    const defaultSuccessHandler = () => {
        queryClient.invalidateQueries([RQ_ALL_LIST]);
        queryClient.invalidateQueries([RQ_LIST_ENTRIES]);
        addAlert({ id: `new-list-added-${Date.now()}`, content: t('newListAdded'), status: 'success' });
    };
    return useMutation([RQ_ADD_LIST], postList, {
        ...mutationOptions,
        onSuccess: callAll(defaultSuccessHandler, mutationOptions.onSuccess),
        onError: (error: any, variables, context) => {
            const param: string | undefined = error?.data?.error?.param ?? error?.response?.data?.error?.param;
            const { name } = variables;

            switch (error?.status || error?.response?.status) {
                /**
                 * By default, the only time we should get a 409
                 * is if a list with the same name already exist
                 * if by some chance that we get a param that isn't name
                 * fire off the default error handler
                 */
                case 409:
                    if (param !== 'name') {
                        addTechnicalErrorModal();
                    } else {
                        const title = t('Error adding list');
                        addTechnicalErrorModal(`${t('List with the name')} ${name} ${t('already exists.')}`, title);
                    }
                    break;
                default:
                    addTechnicalErrorModal();
                    break;
            }

            mutationOptions?.onError?.(error, variables, context);
        }
    });
}

export default useAddNewList;
